/**
 * Shell visual compartido de las pantallas de acceso (login, reset, cambio forzado).
 */
import React, { useEffect, useState } from 'react';
import companyLogo from '../assets/logo.png';
import { ink, studio } from './admin/studioUi';

export const AUTH_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081';
export const AUTH_APP_VERSION = '2.2.0';

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const BRAND = '#2d0b57';

const FOCUS_CSS = `
.auth-shell input:focus, .auth-shell button:focus-visible {
  outline: 2px solid ${ink.accent};
  outline-offset: 2px;
}
.auth-shell input, .auth-shell button { font-family: inherit; }
`;

export function normalizeEmail(value) {
    return String(value || '').trim().toLowerCase();
}

export function isValidEmail(value) {
    const email = normalizeEmail(value);
    if (!email || email.length > 254) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function validateNewPassword(pass) {
    const errors = [];
    if (!pass || pass.length < 8) errors.push('tener al menos 8 caracteres');
    if (!/[A-Z]/.test(pass || '')) errors.push('incluir una mayúscula');
    if (!/[a-z]/.test(pass || '')) errors.push('incluir una minúscula');
    if (!/[0-9]/.test(pass || '')) errors.push('incluir un número');
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(pass || '')) errors.push('incluir un símbolo');
    return errors;
}

function useWideLayout() {
    const [wide, setWide] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 860 : true));
    useEffect(() => {
        const onResize = () => setWide(window.innerWidth >= 860);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return wide;
}

export function AuthNotice({ type, children }) {
    if (!children) return null;
    const ok = type === 'ok';
    return (
        <p
            role={ok ? 'status' : 'alert'}
            style={{
                margin: '0 0 16px',
                padding: '12px 14px',
                borderRadius: '12px',
                fontSize: '0.9rem',
                lineHeight: 1.45,
                textAlign: 'left',
                backgroundColor: ok ? '#e6f4ea' : ink.dangerSoft,
                color: ok ? '#137333' : ink.danger,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: ok ? '#ceead6' : '#f5c6c2',
            }}
        >
            {children}
        </p>
    );
}

export function PasswordToggle({ show, onToggle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            style={styles.eyeBtn}
        >
            {show ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M3 3l18 18" stroke={ink.muted} strokeWidth="1.7" strokeLinecap="round" />
                    <path d="M10.6 10.6A2 2 0 0012 14a2 2 0 001.4-.6M9.9 5.1A10.5 10.5 0 0112 5c5 0 8.5 4 9.7 6.2a1.2 1.2 0 010 1.2 12 12 0 01-3.1 3.5M6.1 6.1A12 12 0 002.3 12.4a1.2 1.2 0 000 1.2A10.5 10.5 0 0012 19c1.3 0 2.5-.2 3.6-.6" stroke={ink.muted} strokeWidth="1.7" strokeLinecap="round" />
                </svg>
            ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M2.3 12.4a1.2 1.2 0 010-1.2A10.5 10.5 0 0112 5c5 0 8.5 4 9.7 6.2a1.2 1.2 0 010 1.2A10.5 10.5 0 0112 19c-5 0-8.5-4-9.7-6.6z" stroke={ink.muted} strokeWidth="1.7" />
                    <circle cx="12" cy="12" r="2.6" stroke={ink.muted} strokeWidth="1.7" />
                </svg>
            )}
        </button>
    );
}

export function AuthField({ label, children, htmlFor }) {
    return (
        <div style={{ marginBottom: 14, textAlign: 'left' }}>
            <label htmlFor={htmlFor} style={studio.fieldLabel}>{label}</label>
            {children}
        </div>
    );
}

export function AuthShell({ kicker = 'Portal de Reportes', title, subtitle, children }) {
    const wide = useWideLayout();
    return (
        <div className="auth-shell" style={{ ...styles.shell, gridTemplateColumns: wide ? 'minmax(280px, 40%) 1fr' : '1fr' }}>
            <style>{FOCUS_CSS}</style>
            <aside style={styles.brand}>
                <div>
                    <img src={companyLogo} alt="Central Media" style={styles.logo} />
                    <p style={styles.brandKicker}>Central Media</p>
                    <h1 style={styles.brandTitle}>Portal de Reportes</h1>
                    <p style={styles.brandCopy}>Acceso interno para el equipo y clientes autorizados.</p>
                </div>
                <p style={styles.brandFoot}>Versión {AUTH_APP_VERSION}</p>
            </aside>
            <main style={styles.main}>
                <div style={styles.card}>
                    <p style={studio.kicker}>{kicker}</p>
                    <h2 style={{ ...studio.title, fontSize: '1.55rem' }}>{title}</h2>
                    {subtitle ? <p style={{ ...studio.subtitle, maxWidth: 'none', marginBottom: 22 }}>{subtitle}</p> : <div style={{ height: 18 }} />}
                    {children}
                </div>
            </main>
        </div>
    );
}

const styles = {
    shell: {
        minHeight: '100vh',
        display: 'grid',
        backgroundColor: ink.wash,
        fontFamily: FONT,
        color: ink.text,
    },
    brand: {
        backgroundColor: BRAND,
        color: '#fff',
        padding: '40px 36px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '220px',
        boxSizing: 'border-box',
    },
    logo: { width: 168, maxWidth: '70%', height: 'auto', marginBottom: 28 },
    brandKicker: {
        margin: 0,
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: '#c5b3f0',
    },
    brandTitle: {
        margin: '10px 0 0',
        fontSize: '1.85rem',
        fontWeight: 600,
        letterSpacing: '-0.03em',
        lineHeight: 1.2,
    },
    brandCopy: {
        margin: '12px 0 0',
        color: 'rgba(255,255,255,0.72)',
        fontSize: '0.95rem',
        lineHeight: 1.5,
        maxWidth: 320,
    },
    brandFoot: {
        margin: '32px 0 0',
        fontSize: '0.78rem',
        color: 'rgba(255,255,255,0.45)',
        letterSpacing: '0.04em',
    },
    main: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '36px 20px',
        boxSizing: 'border-box',
    },
    card: {
        width: '100%',
        maxWidth: 420,
        backgroundColor: ink.surface,
        borderRadius: 20,
        padding: '32px 28px 28px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        boxSizing: 'border-box',
    },
    eyeBtn: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 36,
        height: 36,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderStyle: 'none',
        borderColor: 'transparent',
        borderRadius: 10,
        cursor: 'pointer',
        color: ink.muted,
        padding: 0,
    },
};

export default AuthShell;
